import A from 'components/atoms/A/A';
import Heading from 'components/atoms/Heading/Heading';
import Txt from 'components/atoms/Txt/Txt';
import Carousel from 'components/molecules/Carousel/Carousel';
import MerchantCard from 'components/molecules/MerchantCard/MerchantCard';
import MoreSurveysBanner from 'components/molecules/MoreSurveysBanner/MoreSurveysBanner.qmee';
import DailyPonderBanner from 'components/organisms/DailyPonderBanner';
import PopQuizBanner from 'components/organisms/PopQuizBanner';
import { useCountdownTimer } from 'hooks/useCountdownTimer';
import useSurveyFilterInputs from 'hooks/useSurveyFilterInputs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import BlogPostItem from '../Blog/BlogPostItem.qmee';
import { BLOG, CASHBACK, DEALS, EARN, GAMING, REFER } from '../pages';
import classes from './Dashboard.module.scss';
import ReferralsSummaryCard from './ReferralsSummaryCard.qmee';
import { SummaryCard } from './SummaryCard.qmee';
import { useDashboardQuery } from './useDashboardQuery';

export interface Props {}

type SectionHeaderProps = {
  title: string;
  href: string;
  actions?: React.ReactNode;
};

const SUMMARY_CARD_CONFIG = [
  { title: 'Surveys', tokens: ['survey'], linkTo: EARN.path },
  { title: 'Cashbacks', tokens: ['cashback'], linkTo: CASHBACK.path },
  { title: 'Gaming', tokens: ['gaming', 'game'], linkTo: GAMING.path },
] as const;

function SectionHeader({ title, href, actions }: SectionHeaderProps) {
  return (
    <div className={classes.sectionHeader}>
      <div className={classes.sectionHeaderMain}>
        <Heading level={2} className={classes.sectionTitle}>
          {title}
        </Heading>
        <Txt component={A} href={href} className={classes.viewAllLink}>
          view all
        </Txt>
      </div>
      {actions ? <div className={classes.sectionHeaderActions}>{actions}</div> : null}
    </div>
  );
}

const Dashboard: React.FC<Props> = () => {
  const { t } = useTranslation('dashboard');
  const [filterInputs] = useSurveyFilterInputs();

  const data = useDashboardQuery({
    input: { ...filterInputs, trackingParams: [] },
    first: 4,
  });

  const polls = data.polls?.edges ?? [];

  const classifiedPolls = polls.flatMap((edge) => {
    const node = edge?.node;
    if (!node) return [];
    const isQuiz = !!node.reward?.formatted;
    const live = node.startDate ? new Date(node.startDate) < new Date() : false;
    return [{ node, isQuiz, live }];
  });

  const popQuiz = classifiedPolls.find(({ isQuiz, live }) => isQuiz || !live);
  const dailyPonder = classifiedPolls.find(({ isQuiz, live }) => !isQuiz && live);

  const popQuizPoll = popQuiz?.node;
  const dailyPonderPoll = dailyPonder?.node;
  const isPopQuizLive = popQuiz?.live ?? false;

  const currency = data.walletBalance!.amount.currency;

  const summaryCards = SUMMARY_CARD_CONFIG.map(({ title, tokens, linkTo }) => {
    const currentMonth = new Date().toISOString().slice(0, 7);
    const amount = (data.history?.monthlyEarnings ?? []).reduce((sum, item) => {
      // API `date` may be `YYYY-MM`, `YYYY-MM-DD`, or a full ISO timestamp
      // depending on backend; `startsWith` covers all three.
      if (!item.date?.startsWith(currentMonth)) return sum;
      const itemType = item.type?.toLowerCase() ?? '';

      return tokens.some((token) => itemType.includes(token))
        ? sum + (item.amount?.cents ?? 0)
        : sum;
    }, 0);

    return {
      title,
      linkTo,
      current: {
        amount: amount / 100,
        currency,
      },
    };
  });

  const cashbackCards = (data.desktopCashbacks?.edges ?? []).map(({ node }) => ({
    id: node.id,
    merchantName: node.advertiser?.name ?? '',
    title: node.rewardLabel
      ? t('Earn {{rewardLabel}} Cashback', { rewardLabel: node.rewardLabel })
      : node.title,
    bannerImageSrc: node.advertiser?.imageUrl ?? node.desktopImageUrl ?? node.imageUrl,
    offerUrl: `${CASHBACK.path}/${node.guid}`,
    description: node.description ?? undefined,
    tags: [...node.tags],
  }));

  const dealCards = (data.deals?.edges ?? []).map(({ node }) => ({
    id: node.id,
    merchantName: node.advertiser?.name ?? '',
    title: node.rewardLabel
      ? t('Earn {{rewardLabel}} Cashback', { rewardLabel: node.rewardLabel })
      : node.title,
    bannerImageSrc: node.advertiser?.imageUrl ?? node.desktopImageUrl ?? node.imageUrl,
    offerUrl: `${DEALS.path}/${node.guid}`,
    description: node.description ?? undefined,
    tags: [...node.tags],
  }));

  const blogCards = (data.blogPosts?.edges ?? []).map(({ node }) => node);

  const formatStartTime = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const { clearCountdown } = useCountdownTimer('secondFactorCountdown');

  React.useEffect(() => {
    clearCountdown();
  }, [clearCountdown]);

  return (
    <div className={classes.root}>
      <section className={classes.section}>
        <div className={classes.bannerContainer}>
          <MoreSurveysBanner
            profileUrlRef={data}
            className={classes.moreSurveysBanner}
            version="dashboard"
          />
        </div>
      </section>

      <section className={`${classes.section} ${classes.summarySection}`}>
        <Heading level={2} className={classes.eyebrow}>
          Earnings this month
        </Heading>
        <div className={classes.summaryGrid}>
          {summaryCards.map((card) => (
            <SummaryCard key={card.title} {...card} />
          ))}
          <ReferralsSummaryCard linkTo={REFER.path} currency={currency} />
        </div>
      </section>

      {/* <section className={classes.section}>
        <ExploreCard
          completedTasks={0}
          totalTasks={11}
          href={MORE.path}
          className={classes.exploreCard}
        />
      </section> */}

      {(dailyPonderPoll || popQuizPoll) && (
        <section className={classes.section}>
          <div className={classes.activityBannersContainer}>
            {dailyPonderPoll ? (
              <DailyPonderBanner
                title={t('Daily Ponder')}
                pollId={dailyPonderPoll.id}
                question={dailyPonderPoll.description.title}
                ctaText={dailyPonderPoll.description.subtitle ?? t('Share Your Preference')}
                showInfoButton={false}
                className={classes.activityBanner}
                headingLevel={2}
                headerTitleClassName={classes.sectionTitle}
              />
            ) : null}
            {popQuizPoll ? (
              <PopQuizBanner
                title={t('Pop Quiz')}
                pollId={popQuizPoll.id}
                question={popQuizPoll.description.title}
                description={popQuizPoll.description.subtitle ?? undefined}
                startDate={popQuizPoll.startDate}
                startMessage={
                  isPopQuizLive
                    ? t('Live now!')
                    : t('Starts today at {{time}}', {
                        time: formatStartTime(popQuizPoll.startDate),
                      })
                }
                showInfoButton={false}
                className={classes.activityBanner}
                headingLevel={2}
                headerTitleClassName={classes.sectionTitle}
              />
            ) : null}
          </div>
        </section>
      )}

      <section className={classes.section}>
        <div className={classes.mobileCarousel}>
          <Carousel
            ariaLabel="latest cashbacks"
            slideClassName={classes.carouselSlide}
            dotsClassName={classes.carouselDots}
            renderHeader={(dots) => (
              <SectionHeader title="Latest Cashbacks" href={CASHBACK.path} actions={dots} />
            )}
          >
            {cashbackCards.map((card) => (
              <MerchantCard key={card.id} {...card} className={classes.offerCard} />
            ))}
          </Carousel>
        </div>
      </section>

      <section className={classes.section}>
        <div className={classes.mobileCarousel}>
          <Carousel
            ariaLabel="latest deals"
            slideClassName={classes.carouselSlide}
            dotsClassName={classes.carouselDots}
            renderHeader={(dots) => (
              <SectionHeader title="Latest Deals" href={DEALS.path} actions={dots} />
            )}
          >
            {dealCards.map((card) => (
              <MerchantCard key={card.id} {...card} className={classes.offerCard} />
            ))}
          </Carousel>
        </div>
      </section>

      <section className={classes.section}>
        <div className={classes.mobileCarousel}>
          <Carousel
            ariaLabel="latest blog posts"
            slideClassName={classes.carouselSlide}
            dotsClassName={classes.carouselDots}
            renderHeader={(dots) => (
              <SectionHeader title="Latest Blog" href={BLOG.path} actions={dots} />
            )}
          >
            {blogCards.map((card) => (
              <div className={classes.carouselCard} key={card.id}>
                <BlogPostItem postRef={card} />
              </div>
            ))}
          </Carousel>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
